import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../services/api';
import './css/searchComponent.css';

const { Search } = Input;
const { Option } = Select;

const isMobile = window.innerWidth <= 768;
const mobileSearchClass = isMobile ? 'custom-search-mobile' : '';

const Affiliates = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');

  const fetchAffiliates = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const response = await api.get('/affiliates/admin', {
        params: {
          page,
          limit: pagination.pageSize,
          search
        }
      });
      setAffiliates(response.data.data);
      setPagination(prev => ({
        ...prev,
        current: page,
        total: response.data.total
      }));
    } catch (error) {
      message.error('Erro ao carregar afiliados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const handleTableChange = (pagination) => {
    fetchAffiliates(pagination.current, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchAffiliates(1, value);
  };

  const showModal = (affiliate = null) => {
    setEditingAffiliate(affiliate);
    setModalVisible(true);
    if (affiliate) {
      form.setFieldsValue(affiliate);
    } else {
      form.resetFields();
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingAffiliate) {
        await api.put(`/affiliates/admin/${editingAffiliate.id}`, values);
        message.success('Afiliado atualizado com sucesso');
      } else {
        await api.post('/affiliates/admin', values);
        message.success('Afiliado criado com sucesso');
      }
      setModalVisible(false);
      fetchAffiliates(pagination.current, searchText);
    } catch (error) {
      message.error(error.response?.data?.message || 'Erro ao salvar afiliado');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/affiliates/admin/${id}`);
      message.success('Afiliado inativado com sucesso');
      fetchAffiliates(pagination.current, searchText);
    } catch (error) {
      message.error('Erro ao inativar afiliado');
    }
  };

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Telefone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Código',
      dataIndex: 'affiliate_code',
      key: 'affiliate_code',
      render: (code) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      render: (type) => <Tag color="green">{type}</Tag>
    },
    {
      title: 'Comissão (%)',
      dataIndex: 'commission_rate',
      key: 'commission_rate',
      render: (rate) => `${rate}%`
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'ATIVO' ? 'green' : 'red'}>
          {status}
        </Tag>
      )
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => showModal(record)}
          />
          <Popconfirm
            title="Tem certeza que deseja inativar este afiliado?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 0,
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <Search
          className={mobileSearchClass}
          placeholder="Buscar afiliados..."
          allowClear
          onSearch={handleSearch}
          style={{ width: isMobile ? '100%' : 300 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => showModal()}
        >
          Novo Afiliado
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={affiliates}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
      />

      <Modal
        title={editingAffiliate ? 'Editar Afiliado' : 'Novo Afiliado'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Nome"
            rules={[{ required: true, message: 'Nome é obrigatório' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email é obrigatório' },
              { type: 'email', message: 'Email inválido' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Telefone"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="affiliate_code"
            label="Código do Afiliado"
            rules={[{ required: true, message: 'Código é obrigatório' }]}
          >
            <Input placeholder="Ex: JOAO2024" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Tipo"
            rules={[{ required: true, message: 'Tipo é obrigatório' }]}
          >
            <Select>
              <Option value="AFILIADO">Afiliado</Option>
              <Option value="JOGADOR">Jogador</Option>
              <Option value="INFLUENCER">Influencer</Option>
              <Option value="ORGANIZADOR">Organizador</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="commission_rate"
            label="Taxa de Comissão (%)"
            rules={[{ required: true, message: 'Taxa é obrigatória' }]}
          >
            <Input type="number" min="0" max="100" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Status é obrigatório' }]}
          >
            <Select>
              <Option value="ATIVO">Ativo</Option>
              <Option value="INATIVO">Inativo</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{marginTop: 28}}>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingAffiliate ? 'Atualizar' : 'Criar'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Affiliates;